/**
 * Customer cell widgets (#1805 slice A4a).
 *
 * The three columns that carried a bespoke `fields[].render` land here as named
 * widgets, because `render` has no v2 carrier at all — `ModelField` has no slot
 * for it, and nothing downstream consults one: the v2 cell path goes straight to
 * the resolved widget (`packages/resource-kit-ui/react/columns.ts::createColumns`), and
 * the one surviving reader short-circuits AHEAD of the widget in the text
 * projection (`packages/resource-kit-ui/react/registry/export-text.ts::formatFieldTextForExport`).
 * Keeping both would leave the widget dead code that looks live.
 *
 * Two of them need something the legacy props gave for free:
 *
 *   `email`     → branched on `RendererContext.mode`. The v2 axis is
 *                 `WidgetProps.surface`, set as `surface: mode` by
 *                 `displayProps` in
 *                 `packages/resource-kit-ui/react/registry/field-type-registry.tsx::displayProps`,
 *                 with `'detail'` originating in the `detailColumns` memo in
 *                 `packages/resource-kit-ui/react/resource/ResourcePage.tsx`.
 *   `phone`     → reads the RECORD, not the value: ~1209 customers have a NULL
 *                 `phone` column while the selected address carries one. Its
 *                 `csvText` deliberately does NOT do that — a `render` field
 *                 exported `stringifyRaw(value)`, and exporting the fallback
 *                 would put a phone number in a column that never had one.
 *
 * All three declare `csvText: rawCsv`, which is exactly the text the
 * bespoke-renderer branch of `formatFieldTextForExport` produced
 * (`packages/resource-kit-ui/react/registry/export-text.ts::formatFieldTextForExport`). Omitting it
 * would silently re-route the export
 * through the field TYPE and change what operators download.
 *
 * NOTHING here may import a service. `src/features/widgets.ts` is evaluated by
 * `src/test-setup.ts` for EVERY suite, before any `vi.mock` factory runs, so a
 * service reached from this module is instantiated against the real `@lib/api`
 * and every suite that mocks the HTTP client starts hitting the network. That is
 * why the address helpers moved OUT of `./schema.tsx` (which imports
 * `@/services`) and live here instead.
 */
import { CopyButton, EntityLink, ExternalServiceLink, stringifyRaw } from '@resource-kit/ui/react';
import { registerWidget } from '@resource-kit/controllers';
import type { WidgetCsvContext, WidgetProps } from '@resource-kit/model';
import { EXTERNAL_URLS } from '@/constants/config';
import { AddressLink } from '@components/ui/AddressLink';
import i18n from '@/i18n';

declare module '@resource-kit/core' {
  interface WidgetRegistry {
    customerEmail: { types: 'email'; modes: 'list' };
    customerPhone: { types: 'phone'; modes: 'list' };
    customerAddresses: { types: 'json'; modes: 'list' };
  }
}

export interface CustomerAddress {
  default?: boolean;
  address1?: string;
  city?: string;
  province?: string;
  country?: string;
  zip?: string;
  phone?: string;
}

/** Parse a raw `addresses` value (JSON string or already-parsed array) into a
 *  typed array; anything malformed / non-array yields an empty list. */
const parseAddresses = (raw: unknown): CustomerAddress[] => {
  if (Array.isArray(raw)) return raw as CustomerAddress[];
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as CustomerAddress[]) : [];
    } catch {
      return [];
    }
  }
  return [];
};

/** Count the non-empty display-relevant fields on an address. */
const addressCompleteness = (addr: CustomerAddress): number =>
  [addr.address1, addr.city, addr.province, addr.zip, addr.phone].filter(Boolean).length;

/**
 * Select the address to display for a customer.
 *
 * A flagged `default:true` element wins (first one, if several). Otherwise —
 * the ~727 legacy GraphQL-synced customers where NO element is flagged and the
 * FIRST element is often a sparse stub — pick the most complete element
 * (completeness = count of non-empty [address1, city, province, zip, phone]),
 * ties resolving to the first. Returns `null` for an empty list.
 */
export const pickDisplayAddress = (addresses: CustomerAddress[]): CustomerAddress | null => {
  if (addresses.length === 0) return null;
  const flagged = addresses.find((addr) => addr?.default);
  if (flagged) return flagged;
  let best = addresses[0];
  let bestScore = addressCompleteness(best);
  for (const addr of addresses.slice(1)) {
    const score = addressCompleteness(addr);
    if (score > bestScore) {
      best = addr;
      bestScore = score;
    }
  }
  return best;
};

/** The legacy `render` carrier's CSV projection, kept exactly. */
const rawCsv = ({ value }: WidgetCsvContext): string => stringifyRaw(value);

/**
 * The two record members these widgets read. Written against `unknown` so a row
 * missing either degrades exactly as the legacy renderer's `undefined` did,
 * rather than throwing inside a cell.
 */
type CustomerRow = { shopify_id?: unknown; addresses?: unknown };

function rowOf(record: unknown): CustomerRow {
  if (typeof record !== 'object' || record === null) return {};
  const row: CustomerRow = record;
  return row;
}

function CustomerEmail({ value, record, surface }: WidgetProps) {
  const email = value as string;
  if (!email) return <span className="text-muted-foreground">-</span>;
  if (surface === 'detail') return <span className="text-sm text-foreground">{email}</span>;
  const shopifyId = String(rowOf(record).shopify_id);
  const shopifyUrl = EXTERNAL_URLS.shopifyAdmin('customers', shopifyId);
  return (
    <div className="flex items-center gap-1.5 max-w-[200px]">
      <EntityLink to={`/customers/${shopifyId}`} className="text-sm truncate">
        {email}
      </EntityLink>
      <CopyButton value={email} />
      {shopifyUrl && (
        <ExternalServiceLink href={shopifyUrl} ariaLabel={i18n.t('ui.openInShopify')} />
      )}
    </div>
  );
}

function CustomerPhone({ value, record }: WidgetProps) {
  const phone =
    (value as string | null) ??
    pickDisplayAddress(parseAddresses(rowOf(record).addresses))?.phone ??
    null;
  if (!phone) return <span className="text-muted-foreground">-</span>;
  return <span className="text-sm text-foreground">{phone}</span>;
}

function CustomerAddresses({ value }: WidgetProps) {
  if (!value) return <span className="text-muted-foreground">-</span>;

  const defaultAddress = pickDisplayAddress(parseAddresses(value));
  if (!defaultAddress) return <span className="text-muted-foreground">-</span>;

  const fullAddress = [
    defaultAddress.address1,
    defaultAddress.city,
    defaultAddress.province,
    defaultAddress.country,
  ]
    .filter(Boolean)
    .join(', ');

  // #2689: a Google Maps link plus a copy button, wrapping long values instead
  // of truncating behind a hover overlay that phones cannot open.
  if (!fullAddress) return <span className="text-muted-foreground">-</span>;
  return <AddressLink address={fullAddress} />;
}

/** Called by the feature barrel, which the composition root calls once. */
export function registerCustomerWidgets(): void {
  // These rows declare no `input`: resolution refills it from the field type
  // default (ADR-0030).
  registerWidget('customerEmail', {
    display: CustomerEmail,
    csvText: rawCsv,
    supportedTypes: ['email'],
    modes: ['list'],
  });
  registerWidget('customerPhone', {
    display: CustomerPhone,
    csvText: rawCsv,
    supportedTypes: ['phone'],
    modes: ['list'],
  });
  registerWidget('customerAddresses', {
    display: CustomerAddresses,
    csvText: rawCsv,
    supportedTypes: ['json'],
    modes: ['list'],
  });
}
